import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { User } from 'src/app/micro-services/user/models/user';
import { Objective, ObjectiveStatus, ObjectiveType, ObjectiveUnit } from 'src/app/models/objective.model';
import { ObjectiveService } from 'src/app/services/objective.service';
import { UserService } from 'src/app/services/user.service';
import { ObjectiveTypeTooltipModalComponent } from '../objective-type-tooltip-modal/objective-type-tooltip-modal.component';

@Component({
  selector: 'app-objective-form',
  templateUrl: './objective-form.component.html',
  styleUrls: ['./objective-form.component.scss'],
})
export class ObjectiveFormComponent implements OnInit {
  @Input() clientId!: string;
  @Input() objective!: Objective;
  objectiveForm: FormGroup;
  currentUser: User;
  isQuantifiable: boolean = false;
  objectiveTypes = Object.values(ObjectiveType).filter(type => typeof type === 'number');
  objectiveUnits = Object.values(ObjectiveUnit).filter(unit => typeof unit === 'number');

  objectiveTypeLabels = {
    [ObjectiveType.Progressif]: 'Progressif',
    [ObjectiveType.Subjectif]: 'Subjectif',
    [ObjectiveType.Degressif]: 'Dégressif',
    [ObjectiveType.Duree]: 'Durée',
    [ObjectiveType.Frequence]: 'Fréquence',
  };
  
  objectiveUnitLabels = {
    [ObjectiveUnit.KG]: 'Kilogrammes (kg)',
    [ObjectiveUnit.CM]: 'Centimètres (cm)',
    [ObjectiveUnit.KM]: 'Kilomètres (km)',
    [ObjectiveUnit.MIN]: 'Minutes (min)',
    [ObjectiveUnit.REP]: 'Répétitions',
    [ObjectiveUnit.SESSIONS]: 'Sessions',
    [ObjectiveUnit.BPM]: 'Battements par minute (BPM)',
    [ObjectiveUnit.KCAL]: 'Calories (kcal)',
  };

  constructor(
    private modalController: ModalController,
    private formBuilder: FormBuilder,
    private objectiveService: ObjectiveService,
    private userService: UserService,
    private alertController: AlertController,
  ) {
    this.objectiveForm = this.formBuilder.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      type: ['Subjectif', Validators.required],
      targetValue: [null],
      startingValue : [null],
      unit: [null],
    });
  }

  ngOnInit() {
    this.currentUser = this.userService.user;
  
    if (this.objective) {
      const typeValue = this.objective.type
      const unitValue = this.objective.unit
      this.objectiveForm.patchValue({
        title: this.objective.title,
        description: this.objective.description,
        type: typeValue,
        targetValue: this.objective.targetValue,
        startingValue: this.objective.startingValue,
        unit: unitValue
      });
  
      this.isQuantifiable = [ObjectiveType.Progressif, ObjectiveType.Degressif, ObjectiveType.Duree, ObjectiveType.Frequence].includes(this.objective.type);
    }
  }

  close() {
    this.modalController.dismiss();
  }

  saveObjective() {
    if (this.objectiveForm.valid) {
      const targetValue = this.isQuantifiable ? this.objectiveForm.value.targetValue : 10;
      const startingValue = this.isQuantifiable ? this.objectiveForm.value.startingValue : 0;
  
      if (this.objective) {
        const updatedObjective: Objective = {
          ...this.objective,
          title: this.objectiveForm.value.title,
          description: this.objectiveForm.value.description,
          type: this.objectiveForm.value.type,
          unit: this.objectiveForm.value.unit,
          targetValue: targetValue,
          startingValue: startingValue,
          updatedAt: new Date().toISOString(),
        };
  
        this.objectiveService.updateObjective(this.objective.id,updatedObjective).subscribe(
          (res) => {
            this.modalController.dismiss(res, 'confirm');
          },
          (error) => {
            console.error('Erreur lors de la mise à jour:', error);
          }
        );
      } else {
        const newObjective: Objective = {
          id: 0,
          title: this.objectiveForm.value.title,
          description: this.objectiveForm.value.description,
          status: this.objectiveForm.value.status,
          createdById: this.currentUser.id,
          clientId: this.clientId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          progressUpdates: [],
          currentScore: 0,
          targetValue: targetValue,
          startingValue: startingValue,
          type: this.objectiveForm.value.type,
          unit: this.objectiveForm.value.unit
        };

        if (newObjective.type == ObjectiveType.Subjectif) {
          newObjective.unit = ObjectiveUnit.SUBJECTIF;
        }
  
        this.objectiveService.createObjective(newObjective).subscribe(
          (createdObjective) => {
            this.modalController.dismiss(createdObjective, 'confirm');
          },
          (error) => {
            console.error('Erreur lors de la création de l\'objectif:', error);
          }
        );
      }
    }
  }

  onTypeChange() {
    const type = this.objectiveForm.get('type')?.value;
    this.isQuantifiable = type === ObjectiveType.Progressif || type === ObjectiveType.Degressif || type == ObjectiveType.Duree || type === ObjectiveType.Frequence;
  }

  async openTypeTooltip() {
    const modal = await this.modalController.create({
      component: ObjectiveTypeTooltipModalComponent
    });
    return await modal.present();
  }
}
